import { c as _c } from "react/compiler-runtime";
import { AnimateLayoutChanges, defaultAnimateLayoutChanges, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Tooltip } from '@systemeio/ui';
import PricePlanTooltip from 'modules/digital-product/price-plans/components/price-plan-tooltip';
import { PricePlanType } from '@systemeio/types';
import React, { FC } from 'react';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import { BinIcon, ChangeIcon, DragIcon, SettingsIcon } from '@systemeio/ui/icons';
interface PricePlanItemProps {
  pricePlan: PricePlanType;
  onChange: () => void;
  onEdit: () => void;
  onDelete: () => void;
}
const animateLayoutChanges: AnimateLayoutChanges = args => defaultAnimateLayoutChanges({
  ...args,
  wasDragging: true
});
const PricePlanItem: FC<PricePlanItemProps> = t0 => {
  const $ = _c(42);
  const {
    pricePlan,
    onChange,
    onEdit,
    onDelete
  } = t0;
  const {
    t
  } = useLocoTranslation();
  let t1;
  if ($[0] !== pricePlan.id) {
    t1 = {
      id: pricePlan.id,
      animateLayoutChanges
    };
    $[0] = pricePlan.id;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    attributes,
    listeners,
    setNodeRef,
    isDragging,
    transform
  } = useSortable(t1);
  let t2;
  if ($[2] !== transform) {
    t2 = CSS.Translate.toString(transform);
    $[2] = transform;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const t3 = isDragging ? 0.5 : undefined;
  let t4;
  if ($[4] !== t2 || $[5] !== t3) {
    t4 = {
      transform: t2,
      opacity: t3
    };
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const dndStyle = t4;
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <DragIcon />;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const t6 = pricePlan.innerName || "test.price.plan.name";
  let t7;
  if ($[8] !== t6) {
    t7 = <div className="text-brand-primary flex flex-row items-center gap-1 text-sm lg:text-[15px]"><span>{t6}</span></div>;
    $[8] = t6;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== pricePlan || $[11] !== t7) {
    t8 = <div className="bo-color-[rgb(var(--brand-primary-subtle))] flex flex-col gap-3"><PricePlanTooltip pricePlan={pricePlan} label={t7} /></div>;
    $[10] = pricePlan;
    $[11] = t7;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== t) {
    t9 = t("dashboard.funnel.configuration.common.price_plan.change_title");
    $[13] = t;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== onChange) {
    t10 = <ChangeIcon className="hover:fill-text-primary h-[16px] w-[17px] cursor-pointer fill-[rgb(var(--fill-primary))]" onClick={onChange} />;
    $[15] = onChange;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== t10 || $[18] !== t9) {
    t11 = <Tooltip mode="hover" label={t9}>{t10}</Tooltip>;
    $[17] = t10;
    $[18] = t9;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== t) {
    t12 = t("dashboard.funnel.configuration.common.price_plan.edit_title");
    $[20] = t;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== onEdit) {
    t13 = <SettingsIcon className="hover:fill-text-primary h-[16px] w-[17px] cursor-pointer fill-[rgb(var(--fill-primary))]" onClick={onEdit} />;
    $[22] = onEdit;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] !== t12 || $[25] !== t13) {
    t14 = <Tooltip mode="hover" label={t12}>{t13}</Tooltip>;
    $[24] = t12;
    $[25] = t13;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[27] !== onDelete) {
    t15 = <BinIcon className="h-[18px] w-[17px] cursor-pointer" onClick={onDelete} />;
    $[27] = onDelete;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  let t16;
  if ($[29] !== t11 || $[30] !== t14 || $[31] !== t15) {
    t16 = <div className="flex items-center gap-2">{t11}{t14}{t15}</div>;
    $[29] = t11;
    $[30] = t14;
    $[31] = t15;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  let t17;
  if ($[33] !== t16 || $[34] !== t8) {
    t17 = <div className="border-text-secondary/30 flex w-full items-center justify-between rounded-lg border p-2">{t8}{t16}</div>;
    $[33] = t16;
    $[34] = t8;
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  let t18;
  if ($[36] !== attributes || $[37] !== dndStyle || $[38] !== listeners || $[39] !== setNodeRef || $[40] !== t17) {
    t18 = <div ref={setNodeRef} {...listeners} {...attributes} className="flex items-center gap-3" style={dndStyle}>{t5}{t17}</div>;
    $[36] = attributes;
    $[37] = dndStyle;
    $[38] = listeners;
    $[39] = setNodeRef;
    $[40] = t17;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  return t18;
};
export default PricePlanItem;